import React, { useCallback, useMemo, useState } from 'react';
import { Waypoint } from 'react-waypoint';
import { ZINDEX_FIXED_HEADER } from '@tanium/coreui-utils';
import { Box } from '@tanium/react-box';
import styled from '@tanium/react-emotion';
import { useResizeObserver } from '@tanium/react-use-resize-observer';
import { css } from '@tanium/style-system';
import { PAGE_MARGIN } from './style';
import { HeaderBodyDivider, PageFill } from './themeDefinitions';

const Header = styled('header')({
  position: 'absolute',
  top: 0,
  zIndex: ZINDEX_FIXED_HEADER,
});

const transitionShorthand = 'all .3s ease-out';

export const transitionStyleProps = {
  transition: transitionShorthand,
};

const HeaderContent = styled('div', {
  shouldForwardProp: (p) => p !== 'isCollapsed',
})<{ isCollapsed: boolean }>(
  css({
    backgroundColor: PageFill,
    boxSizing: 'border-box',
    overflowX: 'hidden',
    px: `${PAGE_MARGIN}px`,
    transition: `${transitionShorthand}, width 0s`,
    ['&, *' as string]: transitionStyleProps,
  }),
  ({ isCollapsed }) =>
    css({
      pt: isCollapsed ? 1 : `${PAGE_MARGIN}px`,
    }),
);

export interface PageFixedHeaderProps {
  /**
   * Whether the header should be rendered as collapsed or not. This is
   * determined based on the scroll position of the page.
   */
  collapsed: boolean;
}

interface Props {
  header: React.VFC<PageFixedHeaderProps>;
}

const StyledHeader = styled('hr')({
  border: '1px none',
  borderBottomStyle: 'solid',
});

export const FixedHeader = ({ header }: Props) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const [width, setWidth] = useState<number>();

  const containerRef = useResizeObserver(
    useCallback(({ contentRect }) => {
      setWidth(contentRect.width);
    }, []),
  );

  const handleEnter = useCallback(() => {
    setIsCollapsed(false);
  }, []);

  const handleLeave = useCallback(() => {
    setIsCollapsed(true);
  }, []);

  const headerInlineStyle = useMemo<React.CSSProperties>(() => {
    const style: React.CSSProperties = {};

    if (width !== undefined) {
      // Round width down to avoid overlaying border of scrollbar at certain widths
      style.width = Math.floor(width);
    }

    return style;
  }, [width]);

  return (
    <div ref={containerRef}>
      <Waypoint onEnter={handleEnter} onLeave={handleLeave} topOffset={-PAGE_MARGIN} />
      <Header style={headerInlineStyle}>
        <HeaderContent isCollapsed={isCollapsed}>
          {header({ collapsed: isCollapsed })}
          <Box
            as={StyledHeader}
            borderColor={HeaderBodyDivider}
            mt={1}
            mb={0}
            mx={isCollapsed ? '-16px' : 0}
          />
        </HeaderContent>
      </Header>
    </div>
  );
};
